import axiosDefault from 'axios';
import { v4 as uuid } from 'uuid';

import type {
  FdmFile,
  ICDFView,
  InstanceId,
  InstanceOrExternalId,
  ViewVersions,
} from '@cognite/apm-client';
import UploadGCSDefault from '@cognite/gcs-browser-upload';
import type {
  CogniteClient,
  FileFilterProps,
  FileInfo,
  FileUploadResponse,
  ViewReference,
} from '@cognite/sdk';

import type { IdmAnnotation } from '../../features/file-viewer-modal/types';
import { getCdmViewReferencesFromModelVersion } from '../../providers/fdm-services/cdm-view-reference-utils';
import type { NodeRef } from '../view/types';

import {
  createFdmFileForUpsert,
  createFileFromBlobMetadata,
  imageMetadataToRecord,
  mapAnnotationEdgesToIdmAnnotations,
} from './cdm-media-service-utils';
import type { AxiosDependency, UploadGCSDependency } from './cdm-types';
import { fileFilterToFdmFilter } from './file-idm-migrator';
import {
  createFileMetadataBatcher,
  createScheduleFlush,
} from './file-metadata-batcher';
import type { IMediaService } from './media-service-interface';
import type { BlobFile, ImageItemMetadata, MediaUpsertType } from './types';
import { isSupportedDocumentType } from './utils';

export type FileMetadataView = Pick<ICDFView<FdmFile>, 'byId'>;

export type APMClientDependencies = {
  files: Pick<
    ICDFView<FdmFile>,
    'byId' | 'list' | 'search' | 'upsert' | 'delete'
  >;
  appDataInstanceSpace: string;
  sourceDataInstanceSpace: string;
  viewVersions: Pick<ViewVersions, 'ANNOTATION'>;
};

export type CogniteClientDependencies = {
  files: Pick<
    CogniteClient['files'],
    | 'list'
    | 'search'
    | 'retrieve'
    | 'update'
    | 'delete'
    | 'getDownloadUrls'
    | 'upload'
  >;
  instances: {
    query: CogniteClient['instances']['query'];
  };
  post: CogniteClient['post'];
  get: CogniteClient['get'];
  project: string;
};

export const DEFAULT_DATE = new Date(0);

const isFileWithSpace = (file: FdmFile): file is FdmFile & { space: string } =>
  file.space !== undefined;

type FdmFileWithUploadFlag = FdmFile & { isUploaded?: boolean };

const isFdmFileUploaded = (file: FdmFileWithUploadFlag): boolean =>
  Boolean(file.uploaded ?? file.isUploaded);

// CDF sets isUploaded shortly after the upload commits, so a not-yet-uploaded
// result must not be reused by the retries that wait for it.
const isUploadedFile = (file: FdmFile | undefined) =>
  file !== undefined && isFdmFileUploaded(file);

export class CdmMediaService implements IMediaService {
  client: CogniteClientDependencies;
  apmClient: APMClientDependencies;
  appDataInstanceSpace: string;
  sourceDataInstanceSpaces: string[];
  assetSpace: string;
  readonly assetDocumentsCardView: ViewReference;
  readonly instanceSpaces: string[];
  readonly assetInstanceSpace: string;
  private axios: AxiosDependency;
  private UploadGCS: UploadGCSDependency;
  private readonly cogniteFileView?: FileMetadataView;
  private readonly loadFileMetadata: (
    ref: NodeRef
  ) => Promise<FdmFile | undefined>;

  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies,
    appDataInstanceSpace: string,
    sourceDataInstanceSpaces: string[],
    assetSpace: string,
    axios: AxiosDependency = axiosDefault,
    uploadGCS: UploadGCSDependency = UploadGCSDefault,
    assetDocumentsCardView?: ViewReference,
    cogniteFileView?: FileMetadataView
  ) {
    this.client = client;
    this.apmClient = apmClient;
    this.appDataInstanceSpace = appDataInstanceSpace;
    this.sourceDataInstanceSpaces = sourceDataInstanceSpaces;
    this.assetSpace = assetSpace;
    this.axios = axios;
    this.UploadGCS = uploadGCS;
    this.cogniteFileView = cogniteFileView;
    this.assetDocumentsCardView = {
      ...(assetDocumentsCardView ??
        getCdmViewReferencesFromModelVersion().cdmFile),
    };
    this.instanceSpaces = [appDataInstanceSpace, ...sourceDataInstanceSpaces];
    this.assetInstanceSpace = assetSpace;
    this.loadFileMetadata = createFileMetadataBatcher(
      (refs) => this.loadFileMetadataBatch(refs),
      createScheduleFlush(),
      Date.now,
      isUploadedFile
    );
  }
  private loadFileMetadataBatch = async (refs: NodeRef[]) => {
    const configuredFiles = await this.apmClient.files.byId(refs);
    const foundKeys = new Set(
      configuredFiles.flatMap((file) =>
        file.space ? [`${file.space}:${file.externalId}`] : []
      )
    );
    const missingRefs = refs.filter(
      (ref) => !foundKeys.has(`${ref.space}:${ref.externalId}`)
    );
    const fallbackFiles =
      this.cogniteFileView && missingRefs.length > 0
        ? await this.cogniteFileView.byId(missingRefs)
        : [];

    return [...configuredFiles, ...fallbackFiles].filter(isFileWithSpace);
  };

  listAllFiles = async (
    _limit = 100,
    filter?: FileFilterProps
  ): Promise<FdmFile[]> => {
    const response = await this.apmClient.files.list({
      spaces: [this.appDataInstanceSpace, ...this.sourceDataInstanceSpaces],
      filters: fileFilterToFdmFilter(this.assetSpace, filter),
      propertyTree: {
        category: {},
      },
    });

    return response?.items ?? [];
  };

  getAllMedia = async (
    limit = 100,
    filter?: FileFilterProps
  ): Promise<FdmFile[]> => {
    const mimeTypes = [
      'image/png',
      'image/jpeg',
      'video/quicktime',
      'video/mp4',
    ];

    const mediaPromises = mimeTypes.map((mimeType) =>
      this.listAllFiles(limit, {
        ...filter,
        mimeType,
        uploaded: true,
      })
    );
    const images = await Promise.all(mediaPromises);

    return images.flat();
  };

  getBlobByFileExternalId = async (
    fileExternalId: string
  ): Promise<Blob | null> => {
    // For CDM, we need space - use appDataInstanceSpace as fallback
    return this.getBlobByFileInstanceId(
      fileExternalId,
      this.appDataInstanceSpace
    );
  };

  getBlobByFileInstanceId = async (
    externalId: string,
    space: string
  ): Promise<Blob | null> => {
    // This method should be replaced by the js sdk when it's available.
    const fileLinks = await this.client
      .post<{ items: (InstanceId & { downloadUrl: string })[] }>(
        `/api/v1/projects/${this.client.project}/files/downloadlink`,
        {
          data: {
            items: [
              {
                instanceId: {
                  externalId,
                  space,
                },
              },
            ],
          },
        }
      )
      .then((res) => res.data.items);
    const resp = await this.axios.get(fileLinks[0].downloadUrl, {
      responseType: 'blob',
    });
    return resp.data;
  };

  getMediaByUrl = async (imageURL: string) => {
    const resp = await this.client.get<BlobPart>(imageURL, {
      responseType: 'arraybuffer',
    });

    const data = new Blob([resp.data]);
    const url = URL.createObjectURL(data);
    return url;
  };

  getFileByExternalId = async (fileExternalId: string) => {
    // For CDM, we need space - use appDataInstanceSpace as fallback
    return this.getFileByInstanceId(fileExternalId, this.appDataInstanceSpace);
  };

  getFileByInstanceId = async (
    externalId: string,
    space: string
  ): Promise<FileInfo> => {
    const fdmFile = await this.loadFileMetadata({ externalId, space });

    const defaultFilesInfoValues = {
      createdTime: DEFAULT_DATE,
      lastUpdatedTime: DEFAULT_DATE,
      uploaded: false,
      name: '',
    };

    if (fdmFile) {
      return {
        ...defaultFilesInfoValues,
        ...fdmFile,
        uploaded: isFdmFileUploaded(fdmFile),
        id: 0,
      };
    }

    return {
      ...defaultFilesInfoValues,
      id: 0,
      externalId,
    };
  };

  getMediaByFileExternalId = async (fileExternalId: string, space?: string) => {
    let fileInfo: FileInfo | undefined;
    let blob: Blob | null;

    if (!space) {
      throw new Error('Space is required');
    }

    fileInfo = await this.getFileByInstanceId(fileExternalId, space);

    if (!fileInfo.uploaded) {
      throw new Error(
        `File with external ID ${fileExternalId} is not uploaded yet`
      );
    }

    blob = await this.getBlobByFileInstanceId(fileExternalId, space);
    if (!blob)
      throw new Error(
        `No file with an external ID of ${fileExternalId} was found`
      );

    const blobType = fileInfo.mimeType?.startsWith('image') ? 'image' : 'video';
    const blobFile: BlobFile = {
      url: URL.createObjectURL(blob),
      type: blobType,
    };

    return {
      externalId: fileInfo.externalId,
      metadata: { ...fileInfo.metadata },
      fileUrl: blobFile.url,
      url: createFileFromBlobMetadata(blob, fileInfo.metadata),
      space,
    };
  };

  uploadMedia = async ({
    assetIds: _assetIds = [],
    assetInstanceIds = [],
    metadata,
    image,
    author,
    externalId,
    dataSetId: _dataSetId,
    name,
    isAwsProject,
    space,
  }: {
    assetIds?: number[];
    assetInstanceIds?: InstanceId[];
    metadata: ImageItemMetadata;
    image: File;
    author: string;
    externalId?: string;
    dataSetId?: number;
    name?: string;
    isAwsProject: boolean;
    space?: string;
  }) => {
    const newAssetInstanceIds = assetInstanceIds.map((assetInstanceId) => {
      return {
        externalId: assetInstanceId.externalId,
        space: this.assetSpace,
      };
    });
    let uploadUrl;
    const fileForUpsert = createFdmFileForUpsert({
      name: name ?? uuid(),
      mimeType: image.type,
      externalId: externalId ?? '',
      metadata: imageMetadataToRecord(metadata, author),
      assets: newAssetInstanceIds,
      space: space ?? '',
    });
    await this.apmClient.files.upsert([fileForUpsert]);

    const uploadedFile = await this.client
      .post<{ items: FileUploadResponse[] }>(
        `/api/v1/projects/${this.client.project}/files/uploadlink`,
        {
          data: {
            items: [
              {
                instanceId: {
                  externalId,
                  space,
                },
              },
            ],
          },
        }
      )
      .then((res) => res.data.items);
    uploadUrl = uploadedFile[0].uploadUrl;

    const chunkMultiple = Math.min(
      Math.max(
        2, // at least 2 chunks
        Math.ceil((image.size / 20) * 262144) // Calculate the number of 256 KB chunks in 1/20th of the image size
      ),
      200 // no more than 200 chunks
    );
    if (isAwsProject) {
      // AWS projects require a different upload method than UploadGCS allows. We will make a more permanent solution soon.
      await this.axios({
        method: 'PUT',
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        headers: {
          'Content-Type': image.type,
        },
        url: uploadUrl,
        data: image,
      });
    } else {
      await new this.UploadGCS({
        id: 'datastudio-upload',
        url: uploadUrl,
        file: image,
        contentType: image.type,
        chunkSize: 262144 * chunkMultiple,
      }).start();
    }

    return { externalId, space };
  };

  deleteMedia = async ({
    fileInstanceIds,
  }: {
    fileInstanceIds: InstanceOrExternalId[];
  }) => {
    if (fileInstanceIds.length > 0) {
      return this.apmClient.files.delete(fileInstanceIds);
    }
  };

  updateMedia = async ({
    items,
    dataSetId: _dataSetId,
  }: {
    // id is required for changes, and metadata is
    // the only thing that can be changed by the user
    items: MediaUpsertType;
    dataSetId?: number;
  }): Promise<{ items: FdmFile[] }> => {
    const fdmFiles: FdmFile[] = items.map((item) => {
      return {
        externalId: item.externalId!,
        space: item.space,
        metadata: item.metadata,
      };
    });
    await this.apmClient.files.upsert(fdmFiles);
    return { items: fdmFiles };
  };

  getDocuments = async (
    filter?: FileFilterProps & { assetExternalIds?: string[] },
    limit?: number,
    isAkerbpCustomCode = false
  ): Promise<FdmFile[]> => {
    const documents = await this.listAllFiles(limit, filter);
    return documents.filter((document) =>
      isSupportedDocumentType(document, isAkerbpCustomCode)
    );
  };

  searchDocuments = async (
    query: string,
    limit?: number,
    filter?: FileFilterProps,
    isAkerbpCustomCode = false
  ) => {
    let documents: FdmFile[] = [];
    const newFilters = fileFilterToFdmFilter(this.assetSpace, filter, true);

    // Get from apmClient
    documents =
      (await this.apmClient.files.search({
        query,
        filters: newFilters,
        pageSize: limit,
        spaces: [this.appDataInstanceSpace, ...this.sourceDataInstanceSpaces],
        searchOperator: 'AND',
      })) ?? [];

    return documents.filter((document) =>
      isSupportedDocumentType(document, isAkerbpCustomCode)
    );
  };

  getFileAnnotations = async (
    fileExternalId: string
  ): Promise<IdmAnnotation[]> => {
    const viewReference = {
      externalId: 'CogniteDiagramAnnotation',
      space: 'cdf_cdm',
      version: this.apmClient.viewVersions.ANNOTATION,
    };

    const response = await this.client.instances.query({
      with: {
        files: {
          nodes: {
            filter: {
              in: {
                property: ['node', 'externalId'],
                values: [fileExternalId],
              },
            },
          },
        },
        annotations: {
          edges: {
            from: 'files',
            direction: 'outwards',
          },
        },
      },
      select: {
        annotations: {
          sources: [
            {
              source: {
                externalId: 'CogniteDiagramAnnotation',
                space: 'cdf_cdm',
                type: 'view',
                version: this.apmClient.viewVersions.ANNOTATION,
              },
              properties: [
                'confidence',
                'status',
                'startNodeText',
                'startNodeYMax',
                'startNodeYMin',
                'startNodeXMax',
                'startNodeXMin',
                'endNodeText',
                'endNodeYMax',
                'endNodeYMin',
                'endNodeXMax',
                'endNodeXMin',
                'startNodePageNumber',
                'endNodePageNumber',
              ],
            },
          ],
        },
      },
    });

    return mapAnnotationEdgesToIdmAnnotations(
      response.items.annotations,
      viewReference
    );
  };
}

export const createCdmMediaService = (
  ...args: ConstructorParameters<typeof CdmMediaService>
): IMediaService => new CdmMediaService(...args);
